import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { IssueCategorySetting } from '@chatsdet/types/issues';
import { API_URL } from '../config.js';
import { toApiError } from '../lib/api-error';

type IssueCategoryListResponse = {
  ok: boolean;
  data: IssueCategorySetting[];
};

type IssueCategoryResponse = {
  ok: boolean;
  category: IssueCategorySetting;
};

export function useIssueCategoriesQuery() {
  return useQuery<IssueCategoryListResponse>({
    queryKey: ['issue-categories'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/org/issue-categories`, { credentials: 'include' });
      if (!res.ok) {
        throw await toApiError(res, 'Failed to load issue categories');
      }
      return res.json();
    },
  });
}

type CreateIssueCategoryInput = {
  label: string;
  key?: string;
  description?: string;
  instructions?: string;
  sortOrder?: number;
};

export function useCreateIssueCategoryMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateIssueCategoryInput) => {
      const res = await fetch(`${API_URL}/org/issue-categories`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        throw await toApiError(res, 'Failed to create issue category');
      }
      return (await res.json()) as IssueCategoryResponse;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['issue-categories'] });
    },
  });
}

type UpdateIssueCategoryInput = {
  id: string;
  label?: string;
  key?: string;
  description?: string | null;
  instructions?: string | null;
  sortOrder?: number;
  isActive?: boolean;
};

export function useUpdateIssueCategoryMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: UpdateIssueCategoryInput) => {
      const res = await fetch(`${API_URL}/org/issue-categories/${id}`, {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updates),
      });
      if (!res.ok) {
        throw await toApiError(res, 'Failed to update issue category');
      }
      return (await res.json()) as IssueCategoryResponse;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['issue-categories'] });
    },
  });
}
